import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSignalRContext } from '../components/Contexts/SignalRContext'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { offlineDb, type MutationQueueItem } from './db'
import { beginSync, endSync, reportSynced } from './syncStatus'
import { store } from '../state/store'
import { clearProvisionalShift } from '../state/reducers/shiftReducer'
import { refreshLatestShift } from './refreshLatestShift'
import { patchCachedEntityId, patchCachedRowVersion } from './patchCachedRowVersion'
import { syncAjax } from '../ajax/SyncAjax'
import { SyncOperation, SyncStatus } from '../DTOs/Sync/SyncDtos'
import type { SyncRequest, SyncResponse } from '../DTOs/Sync/SyncDtos'

function operationCode(op: MutationQueueItem['operation']) {
  if (op === 'create') return SyncOperation.Create
  if (op === 'update') return SyncOperation.Update
  return SyncOperation.Delete
}

function buildSyncRequest(items: MutationQueueItem[]): SyncRequest {
  const request: SyncRequest = { stoppages: [], loadings: [], diggingPatterns: [], patternBlasts: [], completeShifts: [], geoRehandles: [], operationLocations: [], engineHours: [] }
  for (const item of items) {
    const base = {
      clientId: item.clientId,
      operation: operationCode(item.operation),
      shiftId: item.shiftId,
      rowVersion: item.rowVersion,
      data: convertDatesToUTC(item.data),
    }
    if (item.entity === 'stoppage') {
      const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...safeData } = (base.data ?? {}) as any
      request.stoppages.push({ ...base, data: safeData, stoppageId: item.entityId })
    } else if (item.entity === 'loading') {
      request.loadings.push({ ...base, loadingId: item.entityId })
    } else if (item.entity === 'diggingPattern') {
      request.diggingPatterns.push({ ...base, diggingPatternId: item.entityId })
    } else if (item.entity === 'patternBlast') {
      request.patternBlasts.push({ ...base })
    } else if (item.entity === 'completeShift') {
      request.completeShifts.push({ clientId: item.clientId, shiftId: item.shiftId })
    } else if (item.entity === 'operationLocation') {
      const d = (item.data ?? {}) as { operationId?: number; locationId?: number; operationPlantId?: number }
      request.operationLocations.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        locationId: d.locationId ?? 0,
        shiftId: item.shiftId,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'engineHours') {
      const d = (item.data ?? {}) as { operationId?: number; startEngineHours?: number; endEngineHours?: number; operationPlantId?: number }
      request.engineHours.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        shiftId: item.shiftId,
        startEngineHours: d.startEngineHours,
        endEngineHours: d.endEngineHours,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'geoRehandle') {
      request.geoRehandles.push({ clientId: item.clientId, operation: operationCode(item.operation), geoRehandleId: item.entityId, rowVersion: item.rowVersion, shiftId: item.shiftId, data: item.data })
    }
  }
  return request
}

async function remapQueuedPatternId(tempId: number, realId: number): Promise<void> {
  const queued = await offlineDb.mutationQueue.where('entity').equals('loading').toArray()

  for (const item of queued) {
    const data = item.data as { diggingPatternId?: number } | undefined
    if (!data || data.diggingPatternId !== tempId) continue
    await offlineDb.mutationQueue.update(item.id!, { data: { ...data, diggingPatternId: realId } })
  }
}

async function remapQueuedEntityId(
  entity: MutationQueueItem['entity'],
  tempId: number,
  realId: number,
): Promise<void> {
  const queued = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, tempId])
    .toArray()

  for (const item of queued) {
    const data = item.data && typeof item.data === 'object'
      ? { ...(item.data as Record<string, unknown>) }
      : item.data

    if (data && typeof data === 'object') {
      for (const idField of ['loadingId', 'stoppageId', 'geoRehandleId', 'diggingPatternId']) {
        if ((data as Record<string, unknown>)[idField] === tempId) {
          (data as Record<string, unknown>)[idField] = realId
        }
      }
    }

    await offlineDb.mutationQueue.update(item.id!, { entityId: realId, data })
  }
}

export function useSyncWorker() {
  const queryClient = useQueryClient()
  const { connection } = useSignalRContext()
  const pendingCount = useLiveQuery(() => offlineDb.mutationQueue.count(), []) ?? 0
  const isSyncingRef = useRef(false)

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    try {
      const snapshot = await offlineDb.mutationQueue.orderBy('createdAt').toArray()

      if (snapshot.length) beginSync(snapshot.length)

      if (navigator.onLine
        && store.getState().shift.provisionalShiftDate
        && !snapshot.some(item => item.entity === 'completeShift')) {
        store.dispatch(clearProvisionalShift())
      }

      if (snapshot.length === 0) return

      const staleEntities = new Set<MutationQueueItem['entity']>()

      const processResults = async (results: SyncResponse['results'], items: MutationQueueItem[]) => {
        reportSynced(results.length)
        const byClientId = new Map(items.map(i => [i.clientId, i]))
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          for (const result of results) {
            const item = byClientId.get(result.clientId)
            if (!item) continue
            staleEntities.add(item.entity)

            if (item.operation === 'create'
              && result.status === SyncStatus.Applied
              && typeof result.serverId === 'number'
              && typeof item.entityId === 'number'
              && item.entityId < 0) {
              if (item.entity === 'diggingPattern') {
                await remapQueuedPatternId(item.entityId, result.serverId)
              }
              await remapQueuedEntityId(item.entity, item.entityId, result.serverId)
              patchCachedEntityId(item.entity, item.entityId, result.serverId)
            }

            if (item.operation === 'update' && result.status === SyncStatus.Applied) {
              patchCachedRowVersion(item.entity, item.entityId, result.serverRowVersion)
            }
            if (result.status !== SyncStatus.Applied) {
              await offlineDb.syncIssues.add({
                clientId: result.clientId,
                entity: item.entity,
                operation: item.operation,
                status: result.status,
                entityId: item.entityId,
                shiftId: item.shiftId,
                serverState: result.serverState,
                clientData: item.data,
                createdAt: Date.now(),
              })
            }
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          }
        })
      }

      const quarantine = async (item: MutationQueueItem, code?: number) => {
        staleEntities.add(item.entity)
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          await offlineDb.syncIssues.add({
            clientId: item.clientId,
            entity: item.entity,
            operation: item.operation,
            status: SyncStatus.Error,
            entityId: item.entityId,
            shiftId: item.shiftId,
            serverState: `The server rejected this change${code ? ` (HTTP ${code})` : ''}, so it could not be applied. It has been removed from the queue - please re-enter it.`,
            clientData: item.data,
            createdAt: Date.now(),
          })
          await offlineDb.mutationQueue.where('clientId').equals(item.clientId).delete()
        })
      }

      try {
        const response = await syncAjax.sync(buildSyncRequest(snapshot))
        if (response.success && response.data) {
          await processResults(response.data.results, snapshot)
        } else if (snapshot.length === 1) {
          await quarantine(snapshot[0], response.errors?.[0]?.code)
        } else {
          for (const item of snapshot) {
            const single = await syncAjax.sync(buildSyncRequest([item]))
            if (single.success && single.data) {
              await processResults(single.data.results, [item])
            } else {
              await quarantine(item, single.errors?.[0]?.code)
            }
          }
        }
      } catch {
        return
      }

      for (const entity of staleEntities) {
        if (entity === 'stoppage') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'stoppages'] })
        } else if (entity === 'loading') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'truckLoads'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
        } else if (entity === 'diggingPattern') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
        } else if (entity === 'operationLocation' || entity === 'engineHours') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['operations'] })
        } else if (entity === 'geoRehandle') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandles'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'crushing'] })
        } else if (entity === 'completeShift') {
          store.dispatch(clearProvisionalShift())
          await refreshLatestShift()
          await queryClient.invalidateQueries({ queryKey: ['dataTable'] })
          await queryClient.invalidateQueries({ queryKey: ['chatree'] })
          await queryClient.invalidateQueries({ queryKey: ['production'] })
          await queryClient.invalidateQueries({ queryKey: ['geology'] })
          await queryClient.invalidateQueries({ queryKey: ['shiftExistence'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['operations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'production'] })
          await queryClient.invalidateQueries({ queryKey: ['production'] })
        } else {
          await queryClient.invalidateQueries({ queryKey: ['references', 'blastmasterIds'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
        }
      }
    } finally {
      isSyncingRef.current = false
      endSync()
    }
  }, [queryClient])

  useEffect(() => {
    let active = true
    const trigger = () => { if (active && navigator.onLine) runSync() }

    trigger()
    window.addEventListener('online', trigger)
    connection?.onreconnected(() => { if (active) runSync() })

    const retry = pendingCount > 0 ? setInterval(trigger, 15000) : null

    return () => {
      active = false
      window.removeEventListener('online', trigger)
      if (retry) clearInterval(retry)
    }
  }, [connection, runSync, pendingCount])
}
